import type { SessionRecord } from '@acasa/shared'
import { Card } from '@/components/card'
import { ApiRequestError } from '@/lib/api'
import { useRevokeOtherSessions, useRevokeSession, useSessions } from './use-auth'

/**
 * Where this account is signed in, and how to end one of them.
 *
 * The point of the list is the sentence somebody says to themselves when they
 * read it: "I don't recognise that." It is what turns a lost laptop into two
 * clicks rather than a call to an admin.
 */
export function SessionCard() {
  const sessions = useSessions()
  const revoke = useRevokeSession()
  const revokeOthers = useRevokeOtherSessions()

  const rows = sessions.data?.sessions ?? []
  const others = rows.filter((row) => !row.current).length
  const error = revoke.error ?? revokeOthers.error

  return (
    <Card
      title="Angemeldete Geräte"
      footer="Eine Sitzung endet automatisch nach 7 Tagen ohne Nutzung, spätestens aber nach 14 Tagen. Beim Ändern des Passworts werden alle anderen Geräte abgemeldet."
    >
      <ul className="flex flex-col">
        {rows.map((row) => (
          <li
            key={row.id}
            className="border-line flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b py-2.5 last:border-0"
          >
            <span className="min-w-0">
              <span className="text-ink block truncate text-sm">
                {describeDevice(row.userAgent)}
                {row.current && <span className="text-positive"> — dieses Gerät</span>}
              </span>
              <span className="text-faint block truncate text-[11px]">
                {row.ip ?? 'Adresse unbekannt'} · zuletzt {relative(row.lastSeenAt)} ·{' '}
                {row.method === 'passkey' ? 'Passkey' : 'Passwort + Code'}
              </span>
            </span>

            {!row.current && (
              <button
                type="button"
                disabled={revoke.isPending}
                onClick={() => revoke.mutate(row.id)}
                className="text-faint hover:text-danger shrink-0 text-[11px] underline underline-offset-4 transition-colors disabled:opacity-40"
              >
                abmelden
              </button>
            )}
          </li>
        ))}

        {rows.length === 0 && !sessions.isLoading && (
          <li className="text-muted py-2 text-sm">Keine aktiven Sitzungen.</li>
        )}
      </ul>

      {others > 0 && (
        <button
          type="button"
          disabled={revokeOthers.isPending}
          onClick={() => revokeOthers.mutate()}
          className="border-line hover:border-line-strong text-muted hover:text-ink mt-4 border px-3 py-2 text-xs transition-colors disabled:opacity-40"
        >
          {revokeOthers.isPending
            ? 'wird abgemeldet …'
            : `Alle anderen Geräte abmelden (${others})`}
        </button>
      )}

      {error && (
        <p className="text-danger mt-3 text-xs">
          {error instanceof ApiRequestError ? error.message : 'Fehler'}
        </p>
      )}
    </Card>
  )
}

/**
 * A readable device name from the user agent. Deliberately coarse: the point is
 * recognition ("that's my iPhone"), not forensics, and a full UA string in a
 * list is unreadable.
 */
function describeDevice(userAgent: string | null): string {
  if (!userAgent) return 'Unbekanntes Gerät'

  const os = /iPhone/.test(userAgent)
    ? 'iPhone'
    : /iPad/.test(userAgent)
      ? 'iPad'
      : /Android/.test(userAgent)
        ? 'Android'
        : /Macintosh/.test(userAgent)
          ? 'Mac'
          : /Windows/.test(userAgent)
            ? 'Windows'
            : 'Gerät'

  // Order matters: Edge and Chrome both claim Safari, Chrome claims Safari too.
  const browser = /Edg\//.test(userAgent)
    ? 'Edge'
    : /OPR\//.test(userAgent)
      ? 'Opera'
      : /Firefox\//.test(userAgent)
        ? 'Firefox'
        : /Chrome\//.test(userAgent)
          ? 'Chrome'
          : /Safari\//.test(userAgent)
            ? 'Safari'
            : null

  return browser ? `${os} · ${browser}` : os
}

function relative(iso: string): string {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60_000)
  if (minutes < 2) return 'gerade eben'
  if (minutes < 60) return `vor ${minutes} Min.`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `vor ${hours} Std.`
  return `am ${new Date(iso).toLocaleDateString('de-DE')}`
}

export type { SessionRecord }
